// main.js
import ColumnDeck from './components/ColumnDeck.js';
import CardFocus from './components/CardFocus.js';
import TabJoust from './components/TabJoust.js';
import FunCursor from './components/FunCursor.js';
import LandMap from './components/LandMap.js';
import PageTransition from './components/PageTransition.js';
import VideoInView from './components/VideoInView.js';

document.addEventListener('DOMContentLoaded', () => {
  // One fade from/to white per page change, instead of content arriving in
  // stages. The <head> script has already added .is-loading.
  const pageTransition = new PageTransition();
  pageTransition.bindLinks();

  // Videos play (and download) only while on screen
  new VideoInView().start();

  // Tab joust (start ONCE)
  const joust = new TabJoust({
    debugTabIndex: false,
    titleIcon: '',
  });
  joust.start();
  window.__tabJoust = joust; // handy for inspecting the live instance from devtools

  // Fun cursor (desktop with pointer only — no hover on touch)
  const isMobile = window.matchMedia('(max-width: 900px)').matches;
  if (!isMobile && window.matchMedia('(hover: hover)').matches) {
    const funCursor = new FunCursor({
      attr: 'data-fun-image',
      size: 50,
      offsetX: 18,
      offsetY: 22,
      autoStart: true
    });
  }

  // ColumnDeck (only if present on this page)
  const deckEl = document.getElementById('deck');
  const tabsEl = document.getElementById('tabs');
  let deck = null;

  // The land map only ever shows once the intro has run: before that every
  // column is collapsed, which would otherwise count as "everything closed"
  // and put the map over the loading GIF.
  let introDone = false;
  let syncMap = () => {};

  if (deckEl && tabsEl) {
    deck = new ColumnDeck({
      mount: deckEl,
      tabsMount: tabsEl,
      skipInitialNavSync: true,
    });

    // If you have pre-rendered columns in the DOM:
    document.querySelectorAll('.column, .col').forEach((colEl) => {
      if (typeof deck.adoptColumnElement === 'function') {
        deck.adoptColumnElement(colEl);
      }
    });

    const wait = (ms) => new Promise((r) => setTimeout(r, ms));

    // --- Intro sequence (desktop only) ---
    if (!isMobile) {
      const navButtons = Array.from(tabsEl.querySelectorAll('.deck-nav button[hidden]'));
      const openOrder = ['commercial', 'editorial', 'teaching'];

      (async () => {
        // Show the loading GIF on white first, then warm the cache for the first
        // cards so the columns arrive complete rather than image by image.
        await pageTransition.ready(document.querySelector('.logo__gif') || document);
        await pageTransition.reveal();
        const firstImages = openOrder.flatMap((key) =>
          [...(deck._getColumnByKey(key)?.root.querySelectorAll('.media img') || [])].slice(0, 4));
        await Promise.all([pageTransition.preload(firstImages), wait(500)]);

        // 1) Pop in nav buttons one at a time
        for (const btn of navButtons) {
          btn.hidden = false;
          btn.classList.add('is-popping');
          btn.addEventListener('animationend', () => btn.classList.remove('is-popping'), { once: true });
          await wait(100);
        }

        await wait(600);

        // 2) Claim all column space at once (invisible) so widths stay fixed
        const cols = openOrder.map(key => deck._getColumnByKey(key));
        for (const c of cols) {
          c.root.classList.remove('is-collapsed');
          c.root.classList.add('is-intro-hidden');
        }
        deck._updateVisibleCount();

        // Let flex settle before revealing
        await wait(50);

        // 3) Stagger the visual reveal — each bounces in at its final 1/3 width
        //    Hide that column's nav button as it opens
        for (let i = 0; i < cols.length; i++) {
          const c = cols[i];
          c.root.classList.remove('is-intro-hidden');
          c.root.classList.add('is-opening');
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              c.root.classList.remove('is-opening');
            });
          });

          // Hide the matching nav button
          const key = c.section || c.root.dataset.section;
          const matchBtn = navButtons.find(b => b.getAttribute('data-toggle-column') === key);
          if (matchBtn) matchBtn.hidden = true;

          await wait(350);
        }

        // Hand control back to ColumnDeck
        deck._skipNavSync = false;
        deck._syncReorderAvailability();
        deck._syncNavButtons();

        // The GIF is the loading moment only: once the columns are open, clear
        // it so the land map is what shows when everything is closed.
        if (gifEl) gifEl.style.display = 'none';
        introDone = true;
        syncMap();
      })();
    } else {
      // Mobile: no column intro — the GIF is the loading moment, shown briefly
      // before the first column opens
      (async () => {
        await pageTransition.ready(document.querySelector('.logo__gif') || document);
        await pageTransition.reveal();
        await wait(1400);
        const first = deck._getColumnByKey('commercial');
        if (first) first.root.classList.remove('is-collapsed');
        deck._updateVisibleCount();
        deck._skipNavSync = false;
        deck._syncNavButtons();
        if (gifEl) gifEl.style.display = 'none';
        introDone = true;
        syncMap();
      })();
    }
  }

  // CardFocus for case study overlays
  const cardFocus = new CardFocus({
    deck: deckEl,
    transition: pageTransition,
    getTitle: () => joust.title,
    setTitle: (t) => joust.setTitle(t),
  });

  // Pages without the homepage intro (case studies, contact): reveal once what
  // is on screen has loaded.
  if (!deckEl || !tabsEl) {
    pageTransition.ready(document.querySelector('.cardfocus') || document).then(() => pageTransition.reveal());
  }

  // --- Land map ---
  // Every commercial and editorial card on the page is a land (Teaching has no
  // case studies, so it isn't mapped). Cards carry date/title/weight — see
  // _partials/_column.html; the section comes from the parent column.
  const logoEl = document.querySelector('.logo');
  const gifEl = document.querySelector('.logo__gif');

  // If the loading GIF can't load, hide it rather than showing a broken-image
  // icon and its alt text at logo size while the page waits.
  if (gifEl) {
    const hideBrokenGif = () => { gifEl.style.display = 'none'; };
    if (gifEl.complete && gifEl.naturalWidth === 0) hideBrokenGif();
    else gifEl.addEventListener('error', hideBrokenGif, { once: true });
  }

  const landsFromDOM = () => Array.from(document.querySelectorAll('.deck .col[data-section] .card[data-entry]'))
    .map((card) => ({
      id: card.dataset.entry,
      title: card.dataset.title || card.querySelector('.title')?.textContent?.trim() || '',
      section: card.closest('[data-section]')?.dataset.section || null,
      date: card.dataset.date || '',
      weight: card.dataset.weight || 0,
      crest: card.dataset.funImage || null,
      url: card.querySelector('.card__link')?.getAttribute('href') || null,
    }));

  let landMap = null;

  // Built once the page's fonts are in: the map measures its text grid from
  // the canvas font, and a fallback font measured early would size every cell
  // differently from how it is later drawn.
  const fontsReady = document.fonts
    ? Promise.race([document.fonts.ready.catch(() => {}), new Promise((r) => setTimeout(r, 1500))])
    : Promise.resolve();

  if (logoEl && deckEl) fontsReady.then(() => {
    landMap = new LandMap({
      mount: logoEl,
      lands: landsFromDOM(),
      logo: '/assets/img/logo.svg',        // the wordmark becomes the capital
      onLandClick(land) {
        // Open the project from its card so CardFocus does the work
        const card = document.querySelector(`.card[data-entry="${land.id}"]`);
        const url = card?.querySelector('.card__link')?.getAttribute('href') || land.url;
        if (!url) return;
        if (card) {
          if (land.section && deck) {
            const col = deck._getColumnByKey(land.section);
            if (col && col.root.classList.contains('is-collapsed')) {
              deck.openColumn(land.section, { scroll: false, active: false });
            }
          }
          cardFocus._activateFromCard(card, url);
        } else {
          window.location.href = url;
        }
      },
    });

    // On mobile the closed bars gather at the bottom of the screen; tell the
    // map how tall that stack is so it can stop above them (see .logo in the
    // mobile styles). Measured from the header bars, which don't animate.
    const fitMapAboveBars = () => {
      if (!isMobile) return;
      const stackH = Array.from(deckEl.querySelectorAll('.col')).reduce((sum, col) => {
        const head = col.querySelector('.head');
        return sum + (head ? head.offsetHeight : 0) + parseFloat(getComputedStyle(col).marginBottom || 0);
      }, 0);
      logoEl.style.setProperty('--stack-h', `${Math.round(stackH)}px`);
    };

    // The map shows when every column is closed — after the intro
    syncMap = () => {
      if (introDone && Number(deckEl.dataset.visible || '0') === 0) {
        fitMapAboveBars();
        landMap.show();
        landMap.handleResize();
      } else {
        landMap.hide();
      }
    };
    new MutationObserver(syncMap).observe(deckEl, { attributes: true, attributeFilter: ['data-visible'] });
    syncMap();
    window.addEventListener('resize', () => landMap.handleResize());
  });


  // Landing directly on a case study also discovers that land
  const directEntry = document.querySelector('.cardfocus[data-entry]')?.dataset.entry;
  if (directEntry) LandMap.recordVisit(directEntry);

  // Opening a project reveals its land
  const revealFromCard = (card) => {
    const id = card?.dataset?.entry;
    if (!id) return;
    if (landMap) landMap.reveal(id); else LandMap.recordVisit(id);
  };

  // Hook CardFocus._activateFromCard (first open from the grid)
  const origActivate = cardFocus._activateFromCard.bind(cardFocus);
  cardFocus._activateFromCard = function(card, url) {
    revealFromCard(card);
    return origActivate(card, url);
  };

  // Hook CardFocus._swapTo (moving between projects inside the overlay)
  const origSwapTo = cardFocus._swapTo.bind(cardFocus);
  cardFocus._swapTo = function(card, url) {
    revealFromCard(card);
    return origSwapTo(card, url);
  };

  // Set aspect-ratio on videos once metadata reports true dimensions
  document.addEventListener('loadedmetadata', (e) => {
    const v = e.target;
    if (!(v instanceof HTMLVideoElement)) return;
    if (!v.videoWidth || !v.videoHeight) return;
    const ratio = v.videoWidth + ' / ' + v.videoHeight;
    v.style.aspectRatio = ratio;
    const media = v.closest('.media');
    if (media) media.style.aspectRatio = ratio;
  }, true);

  // Mobile case study info: inject info blocks inline at nearest row gap
  document.addEventListener('click', (e) => {
    const toggle = e.target.closest('[data-info-toggle]');
    if (!toggle) return;

    // A project page keeps its rows in .visual-content; a project opened from
    // home (CardFocus) unwraps them straight into .cardfocus__leftInner
    const cardfocus = toggle.closest('.cardfocus');
    const visual = cardfocus?.querySelector('.visual-content') || cardfocus?.querySelector('.cardfocus__leftInner');
    const panel  = cardfocus?.querySelector('.cardfocus__panel .cardfocus__content');
    if (!visual || !panel) return;

    const setIcon = (txt) => {
      const span = toggle.querySelector('span') || toggle;
      span.textContent = txt;
    };

    const existing = visual.querySelectorAll('.content-wrap.is-info-injected');
    if (existing.length) {
      cardfocus.appendChild(toggle); // detach from injected block before removing
      existing.forEach((el) => el.remove());
      toggle.classList.remove('is-active');
      toggle.setAttribute('aria-expanded', 'false');
      setIcon('i');
      toggle.setAttribute('aria-label', 'Project info');
      return;
    }

    // Any depth: CardFocus copies the panel in with its own wrapper
    const blocks = Array.from(panel.querySelectorAll('.card'))
      .filter((el) => !el.parentElement.closest('.card'));
    if (!blocks.length) return;

    // Image rows only, not the title
    const rows = Array.from(visual.querySelectorAll(':scope > .content-wrap'))
      .filter((el) => !el.querySelector('.cf-title'));
    if (!rows.length) return;

    // Build gap-point Y positions: before first row, between each pair, after last row.
    const points = [];
    points.push(rows[0].getBoundingClientRect().top);
    for (let i = 1; i < rows.length; i++) {
      const a = rows[i - 1].getBoundingClientRect().bottom;
      const b = rows[i].getBoundingClientRect().top;
      points.push((a + b) / 2);
    }
    points.push(rows[rows.length - 1].getBoundingClientRect().bottom);

    const btnRect = toggle.getBoundingClientRect();
    const targetY = btnRect.top + btnRect.height / 2;

    let nearest = 0;
    let minD = Infinity;
    points.forEach((y, i) => {
      const d = Math.abs(y - targetY);
      if (d < minD) { minD = d; nearest = i; }
    });

    const frag = document.createDocumentFragment();
    const wraps = blocks.map((block, j) => {
      const wrap = document.createElement('section');
      wrap.className = 'content-wrap is-info-injected';
      wrap.style.animationDelay = (j * 90) + 'ms';
      const inner = document.createElement('div');
      inner.className = 'content-block';
      inner.appendChild(block.cloneNode(true));
      wrap.appendChild(inner);
      frag.appendChild(wrap);
      return wrap;
    });
    if (nearest === 0) {
      visual.insertBefore(frag, rows[0]);
    } else if (nearest <= rows.length) {
      const after = rows[nearest - 1];
      after.parentNode.insertBefore(frag, after.nextSibling);
    } else {
      visual.appendChild(frag);
    }

    // Attach button to top of the first injected block as a close affordance
    wraps[0].appendChild(toggle);
    toggle.classList.add('is-active');
    toggle.setAttribute('aria-expanded', 'true');
    setIcon('×');
    toggle.setAttribute('aria-label', 'Hide project info');
  });


});
